import { api } from './api';

// --- Blocked Domains ---
export interface BlockedDomain {
  id: string;
  domain: string;
  addedBy: string;
  createdAt: string;
}

export interface BlockedDomainsResponse {
  data: BlockedDomain[];
  total: number;
  page: number;
  limit: number;
}

export const getBlockedDomains = (page = 1, limit = 20) =>
  api.get<never, { data: BlockedDomainsResponse }>('/admin/blocked-domains', { params: { page, limit } });

export const addBlockedDomain = (domain: string) =>
  api.post<never, { data: BlockedDomain }>('/admin/blocked-domains', { domain });

export const removeBlockedDomain = (id: string) =>
  api.delete<never, void>(`/admin/blocked-domains/${id}`);

// --- Student Moderation ---
export interface ModerationStudent {
  id: string;
  name: string;
  email: string;
  parentEmail: string | null;
  emailVerified: boolean;
  parentEmailConfirmed: boolean;
  status: string;
  createdAt: string;
  lastActiveAt: string | null;
}

export interface ListStudentsParams {
  emailVerified?: boolean;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface StudentsListResponse {
  students: ModerationStudent[];
  total: number;
  page: number;
  limit: number;
}

export const getModerationStudents = (params: ListStudentsParams) =>
  api.get<never, { data: StudentsListResponse }>('/admin/students', { params });

export const suspendStudent = (id: string, reason?: string) =>
  api.post<never, { data: ModerationStudent }>(`/admin/students/${id}/suspend`, { reason });

export const unsuspendStudent = (id: string) =>
  api.post<never, { data: ModerationStudent }>(`/admin/students/${id}/unsuspend`);

// --- Teacher Approval ---
export interface TeacherApplication {
  id: string;
  name: string;
  email: string;
  teacherStatus: 'PENDING_APPROVAL' | 'ACTIVE' | 'REJECTED' | 'SUSPENDED';
  teacherRejectionReason?: string | null;
  createdAt: string;
  lastActiveAt: string | null;
}

/** @deprecated use TeacherApplication */
export type PendingTeacher = TeacherApplication;

export interface TeacherApplicationsResponse {
  teachers: TeacherApplication[];
  total: number;
  page: number;
  limit: number;
}

export const getPendingTeachers = (page = 1, limit = 20) =>
  api.get<never, { data: TeacherApplicationsResponse }>('/admin/teachers/pending', { params: { page, limit } });

export const getTeacherApplications = (page = 1, limit = 20, teacherStatus?: string) =>
  api.get<never, { data: TeacherApplicationsResponse }>('/admin/teachers/applications', {
    params: { page, limit, ...(teacherStatus ? { teacherStatus } : {}) },
  });

export const approveTeacher = (id: string) =>
  api.post<never, { data: TeacherApplication }>(`/admin/teachers/${id}/approve`);

export const rejectTeacher = (id: string, reason: string) =>
  api.post<never, { data: TeacherApplication }>(`/admin/teachers/${id}/reject`, { reason });

// --- Bulk Student Import ---
export interface BulkImportResult {
  created: number;
  skipped: number;
  errors: Array<{ row: number; email: string; reason: string }>;
}

export const bulkImportStudents = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<never, { data: BulkImportResult }>('/admin/students/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
